import { createAsyncThunk } from '@reduxjs/toolkit';
import type { PusherEventPayloadMap } from '@timelyapp/integration-cloud-api-spec';
import type { IState } from 'redux/state';
import type { UpdateConnectionIdApiArg } from '~redux/modules/tic/api-slice/GENERATED_ARTIFACT';
import { ticApi } from '~redux/modules/tic/api-slice/tic.apiSlice';
import { getContextualTicAccounts } from '~redux/modules/tic/tic.auth.selectors';
import { showToast } from '~redux/modules/toasts.actions';
import {
  getIntegrationConfiguration,
  getPendingChangesState,
  getSubscriptionQueryArgs,
} from './tic.integrations.selectors';

const FINALIZE_CACHE_REFRESH_DELAY_MS = 2500;

/**
 * Async thunk to save subscription changes
 * This validates configuration safety and dispatches the batch update mutation
 */
export const saveSubscriptionChanges = createAsyncThunk<void, void, { state: IState }>(
  'integrations/saveSubscriptionChanges',
  async (_, { getState, dispatch }) => {
    const state = getState();
    const config = getIntegrationConfiguration(state);
    const pendingChangesState = getPendingChangesState(state);
    const queryArgs = getSubscriptionQueryArgs(state);

    if (!pendingChangesState.canSave) {
      throw new Error('Cannot save subscription changes - validation failed');
    }

    // Dispatch the batch update mutation
    await dispatch(
      ticApi.endpoints.updateBatchOfSubscriptions.initiate({
        provider: config.toolId!,
        accountId: queryArgs?.accountId!,
        connectionId: queryArgs?.connectionId!,
        body: {
          subscriptions: pendingChangesState.forSave,
          // Optionally trigger synchronization after updating subscriptions
          // runOperationId: 'synchronize',
        },
      })
    ).unwrap();

    // The API slice handles clearing pending changes and cache updates optimistically
  }
);

/**
 * Generic thunk to patch the current connection using context from selectors
 * Uses RTK Query cache patterns and provides optimistic updates
 */
export const patchCurrentConnection = createAsyncThunk<
  void,
  Partial<Pick<UpdateConnectionIdApiArg['body'], 'settings' | 'imported_data_access'>>,
  { state: IState }
>('integrations/patchCurrentConnection', async (patchData, { getState, dispatch }) => {
  const state = getState();
  const config = getIntegrationConfiguration(state);
  const ticAccounts = getContextualTicAccounts(state);

  // Get context from selectors - always use workspace.id as accountId
  const toolId = config.toolId;
  const accountId = ticAccounts.workspace?.id;

  if (!toolId || !accountId) {
    throw new Error('Missing required context: toolId or accountId not available from selectors');
  }

  // Find connection from getIntegrationDetails cache (primary data source)
  const integrationDetailsQuery = ticApi.endpoints.getIntegrationDetails.select({
    accountId,
    providerId: toolId,
  })(state);

  const integrationDetails = integrationDetailsQuery.data?.data;
  const connection =
    config.scope === 'personal'
      ? integrationDetails?.personal?.connection
      : integrationDetails?.account?.connection;

  if (!connection?.id) {
    throw new Error('No active connection found for current configuration');
  }

  // Patch the connection with optimistic updates via RTK Query
  await dispatch(
    ticApi.endpoints.updateConnectionId.initiate({
      connectionId: connection.id,
      body: {
        id: connection.id,
        // Preserve existing imported_data_access if not explicitly provided in patch
        imported_data_access:
          patchData.imported_data_access ?? connection.imported_data_access ?? 'default',
        settings: {
          ...connection.settings,
          ...patchData.settings,
        },
      },
    })
  ).unwrap();
});

/**
 * Handle synchronization completion from Pusher events
 * Shows a toast and refreshes caches (setup completion is server-side).
 */
export const handleSynchronizeCompletion = createAsyncThunk<
  void,
  PusherEventPayloadMap['integration_job_event'],
  { state: IState }
>('integrations/handleSynchronizeCompletion', async (payload, { dispatch }) => {
  // Only handle synchronize lifecycle events.
  if (
    payload.requested_operation_id !== 'synchronize' ||
    !payload.connection_id ||
    !payload.tool_id
  )
    return;

  if (payload.status === 'started') return;

  const isSuccess = payload.status === 'completed' && !payload.error;

  dispatch(
    showToast({
      title: isSuccess ? 'Integration import completed' : 'Integration synchronization failed',
      message: isSuccess
        ? 'Data will be available within a few minutes.'
        : payload.error || payload.message || 'Synchronization failed. Please try again.',
      type: isSuccess ? 'success' : 'danger',
    }) as any
  );

  // Refresh caches immediately, and again after a short delay to avoid racing server-side finalize.
  dispatch(
    ticApi.util.invalidateTags(['IntegrationDetails', 'Integrations', 'ConnectionWebhooks'])
  );
  setTimeout(() => {
    dispatch(ticApi.util.invalidateTags(['IntegrationDetails', 'Integrations']));
  }, FINALIZE_CACHE_REFRESH_DELAY_MS);
});
